import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, finalize, timeout } from 'rxjs';
import { IOrderModel} from 'src/app/appModels/IOrderModel';
import { OrderService } from 'src/app/appServices/order.service';

@Injectable({
  providedIn: 'root',
})
export class TransactionResolver {
  selectedOrderType = new BehaviorSubject(null);
  searchModel = new BehaviorSubject(null);
  selectedOrderPaid = new BehaviorSubject('saved');

  orderList = new BehaviorSubject<IOrderModel[]>(null);

  todayDate;
  dayRange;
  private ordersRefreshing = false;
  // The resolver is application-scoped, so it outlives logout/navigation.
  // Increment this whenever the tenant session ends to invalidate responses
  // that were requested for the previous tenant.
  private orderContextVersion = 0;

  constructor(private orderService: OrderService) { }

  updateOrder(order) {
    if (!this.orderList.value) {
      return;
    }
    const orderListCopy = [...this.orderList.value];

    //find index of item to be replaced
    const targetIndex = this.orderList.value.findIndex(
      (f) => f.id === order.id
    );
    if (targetIndex !== -1) {
      //replace the object with a new one.
      orderListCopy[targetIndex] = order;
    }
    this.orderList.next(orderListCopy);
  }

  removeOrderById(id) {
    //create a copy of summer fruits.
    const orderListCopy = [...this.orderList.value];

    //find index of item to be replaced
    const targetIndex = this.orderList.value.findIndex((f) => f.id === id);
    if (targetIndex !== -1) {
      //replace the object with a new one.
      orderListCopy.splice(targetIndex, 1);
    }
    this.orderList.next(orderListCopy);
  }

  getOrderById(id) {
    return this.orderList.value.filter((e) => e.id === id);
  }

  markOrderPaidById(id) {
    const order = this.getOrderById(id);
    order[0].paid = true;
    this.updateOrder(order[0]);
  }

  /**
   * refresh orders
   */
  public refreshOrders() {
    this.getOrdersByApi();
  }

  /** Clear all transaction state for the current tenant session. */
  public clear(): void {
    this.orderContextVersion++;
    // A request from the previous session may still be running. It is no
    // longer allowed to hold the refresh mutex for the next session; its
    // version guard below still prevents its response/finalize from touching
    // the new context.
    this.ordersRefreshing = false;
    this.selectedOrderType.next(null);
    this.searchModel.next(null);
    this.selectedOrderPaid.next('saved');
    this.orderList.next(null);
    this.todayDate = null;
    this.dayRange = null;
  }

  /**
   * get orders by api
   */
  private getOrdersByApi() {
    if (this.ordersRefreshing) {
      return;
    }
    this.ordersRefreshing = true;
    const requestContextVersion = this.orderContextVersion;
    this.todayDate = new Date();
    this.dayRange = {
      from: new Date().setHours(0, 0, 0, 0),
      to: new Date().getTime(),
    };
    this.orderService
      .getOrdersByDays(this.dayRange.from, this.dayRange.to)
      // Without a timeout, a hung request (frozen TCP in the WebView) would
      // leave ordersRefreshing=true forever: finalize never fires and every
      // later refresh (15s tick + pull-to-refresh) is silently dropped, so the
      // order list freezes for the staff. The timeout guarantees finalize runs.
      .pipe(
        timeout(20000),
        finalize(() => {
          if (requestContextVersion === this.orderContextVersion) {
            this.ordersRefreshing = false;
          }
        }),
      )
      .subscribe({
        next: (res) => {
          // A request can complete after logout. Do not allow data fetched for
          // the previous tenant to repopulate the shared order list.
          if (requestContextVersion === this.orderContextVersion) {
            this.setOrders(res);
          }
        },
        error: (err) => {
          console.error('Order list refresh failed:', err);
        },
      });
  }

  /**
   * set orders
   *
   * @param orders
   */
  private setOrders(orders) {
    this.orderList.next(orders);
  }

  /**
   * create cash payment for order
   * @param orderId 
   * @param payload 
   * @returns 
   */
  public markCashOrderAsPaid(orderId, payload): Observable<any> {
    return this.orderService.markCashOrderAsPaid(orderId, payload);
  }



  public updateOrderStatus(order): Observable<any> {
    return this.orderService.updateOrderStatus(order);
  }

  
  /**
     *Get all available Status List from API 
     */
    getAllOrderStatusList(): Observable<any>  {
      return this.orderService.getAllOrderStatusList();
    }
  

  
}
